<script setup lang="ts">
import { computed, markRaw, ref, watch } from 'vue';
import DataTable from '@/components/DataTable/DataTable.vue';
import { isPlainClick } from '@/components/DataTable/rowClick';
import PlaylistSwitchingTableHeader from '@/components/DataTable/PlaylistSwitchingTableHeader.vue';
import PlaylistContainerInspector from '@/components/DataTable/PlaylistContainerInspector.vue';
import ModelRecordOverlayHeader from '@/components/DataTable/ModelRecordOverlayHeader.vue';
import {
  usePlaylistSwitchingSearch,
  usePlaylistSwitchingSummary,
  type PlaylistSwitchingListRow,
} from '@/composables/api/usePlaylistSwitchingApi';
import { usePersistentTable } from '@/composables/usePersistentTable';
import { useRoutedSavedView } from '@/composables/useRoutedSavedView';
import { PLAYLIST_SWITCHING_NUMERIC_COLUMN_IDS } from '@/composables/playlistSwitchingSchema';
import { useInspectorStore } from '@/stores/inspectorStore';
import { useInspectorTopOffset } from '@/composables/useInspectorTopOffset';
import { formatUsMmDdYyTime12h } from '@/utils/date';
import { booleanCellDisplayString } from '@/utils/booleanDisplay';
import type { TableColumn } from '@/types/tableTypes';

/** YYYY-MM-DD for a date `daysAgo` before today (local). */
function isoDaysAgo(daysAgo: number): string {
  const date = new Date();
  date.setDate(date.getDate() - daysAgo);
  return date.toISOString().slice(0, 10);
}

const fromDate = ref(isoDaysAgo(30));
const toDate = ref(isoDaysAgo(0));

// Keep the side-peek inspector below the toolbar row so the date filters and
// paginator stay visible and clickable while it is open.
const toolbarRef = ref<HTMLElement | null>(null);
useInspectorTopOffset(toolbarRef);

const {
  pinnedColumns,
  unpinnedColumns,
  columnSort,
  compositeFilter,
  pageIndex,
  pageSize,
  toggleSort,
  setColumnSize,
  updateColumnState,
} = usePersistentTable();

// This page's saved views live in the section sidebar. Opening one puts it in
// the address (`?view=<id>`), as on a list.
useRoutedSavedView();

const listQuery = usePlaylistSwitchingSearch({
  pageIndex,
  pageSize,
  sorting: columnSort,
  filter: compositeFilter,
  from: fromDate,
  to: toDate,
});

const summaryQuery = usePlaylistSwitchingSummary(
  computed(() => ({ from: fromDate.value, to: toDate.value })),
);

const listData = computed(() => listQuery.data.value);
const summary = computed(() => summaryQuery.data.value);
const totalItems = computed(() => listData.value?.meta?.total ?? 0);

const inspectorStore = useInspectorStore();
const selectedListId = ref<number | null>(null);

// Un-highlight the row when the inspector is dismissed.
watch(
  () => inspectorStore.isOpen,
  (open) => {
    if (!open) {
      selectedListId.value = null;
    }
  },
);

function listLabel(row: PlaylistSwitchingListRow): string {
  return row.name || row.code || `List #${row.location_zone_custom_playlist_id}`;
}

function openInspector(row: PlaylistSwitchingListRow) {
  selectedListId.value = row.location_zone_custom_playlist_id;

  inspectorStore.open(
    listLabel(row),
    markRaw(PlaylistContainerInspector),
    {
      locationZoneCustomPlaylistId: row.location_zone_custom_playlist_id,
      entityId: 'LocationZoneCustomPlaylist',
      // The report is about switching, so land there rather than on Details.
      initialSection: 'switches',
      from: fromDate.value,
      to: toDate.value,
      listLabel: listLabel(row),
    },
    {
      subtitle: row.code ?? undefined,
      // The same heading the Search page gives this inspector. It is not
      // decoration: the container entities keep `location` and `location_zone`
      // out of the Details fields because this heading's zone line names them,
      // so opening the record without it left the venue nowhere on the page —
      // the report's own table has no location column either.
      headerComponent: markRaw(ModelRecordOverlayHeader),
      headerProps: {
        entityId: 'LocationZoneCustomPlaylist',
        recordId: row.location_zone_custom_playlist_id,
      },
    },
  );

}

/**
 * A plain click anywhere on a row opens the list.
 * This table has no checkboxes, so a modified click reaches the page. The page ignores it.
 */
function onTableRowClick(row: Record<string, unknown>, event: MouseEvent) {
  if (!isPlainClick(event)) return;
  openInspector(asRow(row));
}

/** Bridge the loose Record<string, unknown> from DataTable's data prop to the typed row. */
function asRow(record: Record<string, unknown>): PlaylistSwitchingListRow {
  return record as unknown as PlaylistSwitchingListRow;
}

const columnsResizeHandler = (columnId: string, width: number) => setColumnSize(columnId, width);
const columnsSortHandler = (columnField: string) => toggleSort(columnField);
const columnsReorderHandler = (columns: TableColumn[]) => updateColumnState(columns);

const numberFormatter = new Intl.NumberFormat();
function formatNumber(value: number | null | undefined): string {
  return value == null ? '—' : numberFormatter.format(value);
}
// This page's Yes/No/— convention is now the app-wide one; the shared helper is
// where it lives, so the report and the search table cannot drift apart.
function formatBool(value: boolean | null | undefined): string {
  return booleanCellDisplayString(value);
}
function formatDate(value: string | null | undefined): string {
  return formatUsMmDdYyTime12h(value) || '—';
}
</script>

<template>
  <div class="flex flex-1 flex-col min-h-0">
    <div
      ref="toolbarRef"
      class="shrink-0 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/95 z-30"
    >
      <PlaylistSwitchingTableHeader
        v-model:from="fromDate"
        v-model:to="toDate"
        :total-records="totalItems"
      />
    </div>

    <div
      v-if="listQuery.error.value"
      class="border-destructive/50 bg-destructive/10 text-destructive shrink-0 mx-4 mb-2 rounded-md border p-3 text-sm"
    >
      Could not load the playlists report. Try refreshing; if it persists, contact support.
    </div>

    <div class="min-h-0 flex-1 w-full overflow-auto px-4 pb-4 max-md:pb-mobile-area-bar-clearance">
      <DataTable
        data-testid="playlist-switching-table"
        table-in-parent-scroll
        sticky-header
        row-id-key="location_zone_custom_playlist_id"
        :selected-row-id="selectedListId"
        :data="(listData?.data as unknown as Record<string, unknown>[])"
        :total-records="totalItems"
        :columns="unpinnedColumns"
        :pinned-columns="pinnedColumns"
        :is-loading="listQuery.isLoading.value"
        :page-index="pageIndex"
        :page-size="pageSize"
        @columnsResized="columnsResizeHandler"
        @columnsSorted="columnsSortHandler"
        @columnsReordered="columnsReorderHandler"
        @row-click="onTableRowClick"
      >
        <template #cell="{ column, row }">
          <div
            v-if="column.id === 'name'"
            class="truncate text-xs font-medium"
            data-testid="playlist-switching-row"
          >
            {{ asRow(row).name ?? `List #${asRow(row).location_zone_custom_playlist_id}` }}
          </div>
          <div
            v-else-if="column.id === 'code' || column.id === 'source_bucket_name' || column.id === 'status_name'"
            class="truncate text-xs"
            :class="column.id === 'code' ? 'font-mono text-muted-foreground' : ''"
          >
            {{ (row as Record<string, unknown>)[column.key] ?? '—' }}
          </div>
          <div
            v-else-if="column.id === 'shuffle' || column.id === 'user_can_shuffle'"
            class="text-xs"
          >
            {{ formatBool((row as Record<string, unknown>)[column.key] as boolean | null) }}
          </div>
          <div
            v-else-if="['status_expiration', 'created_at', 'updated_at'].includes(column.id)"
            class="whitespace-nowrap text-xs tabular-nums"
          >
            {{ formatDate((row as Record<string, unknown>)[column.key] as string | null) }}
          </div>
          <div
            v-else-if="PLAYLIST_SWITCHING_NUMERIC_COLUMN_IDS.has(column.id)"
            class="text-right text-xs tabular-nums"
          >
            {{ formatNumber((row as Record<string, unknown>)[column.key] as number | null) }}
          </div>
          <div
            v-else
            class="truncate text-xs"
          >
            {{ (row as Record<string, unknown>)[column.key] ?? '—' }}
          </div>
        </template>
      </DataTable>
    </div>

    <!-- Footer status bar (mirrors the Audio File Issues page footer). -->
    <div
      class="text-muted-foreground shrink-0 flex min-h-12 flex-wrap items-center justify-center md:justify-start gap-x-4 md:gap-x-6 gap-y-1 border-t text-sm tabular-nums px-2 md:px-4"
    >
      <div class="flex items-baseline gap-1.5">
        <span class="hidden md:inline text-xs uppercase tracking-wide">Total switches</span>
        <span class="text-foreground font-semibold" title="Total switches">{{ formatNumber(summary?.total_switches) }}</span>
      </div>
      <div class="flex items-baseline gap-1.5">
        <span class="hidden md:inline text-xs uppercase tracking-wide">Lists switched to</span>
        <span class="text-foreground font-semibold" title="Lists switched to">{{ formatNumber(summary?.distinct_lists_switched) }}</span>
      </div>
      <div class="flex items-baseline gap-1.5">
        <span class="hidden md:inline text-xs uppercase tracking-wide">Distinct switchers</span>
        <span class="text-foreground font-semibold" title="Distinct switchers">{{ formatNumber(summary?.distinct_switchers) }}</span>
      </div>
    </div>
  </div>
</template>
